import { TextButton } from '@tfo/common'
import { ICompany } from '@tfo/common/src/models'
import { observer } from 'mobx-react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import CustomerPortalDialog from '../../../../containers/client/common/CustomerPortalDialog'
import $http from '../../../../utils/$http'
import { ScimActiveWithTokenView } from './ScimActiveWithTokenView'
import { ScimConfirmDisableView } from './ScimConfirmDisableView'
import { ScimConfirmEnableView } from './ScimConfirmEnableView'
import { ScimConfirmRegenerateView } from './ScimConfirmRegenerateView'
import { ScimLoadingView } from './ScimLoadingView'
import { ScimManageView } from './ScimManageView'
import {
  MinLoadingTime,
  ScimEnableResponse,
  ScimRotateResponse,
  ScimStatusResponse,
  ScimToken,
  useScimStyles,
  View,
} from './scimShared'

export type { ScimToken, ScimStatusResponse } from './scimShared'

type ScimManagementModalProps = {
  open: boolean
  onClose: () => void
  company: ICompany
  onScimStateChanged?: () => void
}

const ScimManagementModal: React.FC<ScimManagementModalProps> = observer(({ open, onClose, company, onScimStateChanged }) => {
  const intl = useIntl()
  const styles = useScimStyles()

  const [view, setView] = React.useState<View>('loading')
  const [endpointUrl, setEndpointUrl] = React.useState<string | null>(null)
  const [token, setToken] = React.useState<ScimToken | null>(null)
  const [revealedToken, setRevealedToken] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [errorKey, setErrorKey] = React.useState<string | null>(null)
  const [mappingExpanded, setMappingExpanded] = React.useState(false)

  const loadStatus = (showLoading: boolean) => {
    if (showLoading) {
      setView('loading')
    }

    const fetchStatus = $http.get<ScimStatusResponse>(`/authenticate/sso/scim?companyId=${company.id}`)
    const minWait = new Promise(resolve => setTimeout(resolve, showLoading ? MinLoadingTime : 0))

    Promise.allSettled([fetchStatus, minWait])
      .then((values) => {
        if (values[0].status === 'rejected') {
          console.error('Failed to fetch SCIM status:', values[0].reason)
          setErrorKey('sso.scim.error.status')
          setView('confirmEnable')
          return
        }

        const response = values[0].value as ScimStatusResponse
        setEndpointUrl(response.scimEndpointUrl)
        setToken(response.tokens?.[0] ?? null)
        setView(response.status === 'Active' ? 'manage' : 'confirmEnable')
      })
  }

  React.useEffect(() => {
    if (open) {
      setRevealedToken(null)
      setErrorKey(null)
      setMappingExpanded(false)
      loadStatus(true)
    }
  }, [open])

  const enableScim = () => {
    setErrorKey(null)
    setIsSubmitting(true)
    $http.post<ScimEnableResponse>('/authenticate/sso/scim', { companyId: company.id })
      .then((response) => {
        setRevealedToken(response.bearerToken)
        setEndpointUrl(response.scimEndpointUrl)
        setView('activeWithToken')
        onScimStateChanged?.()
      })
      .catch((error) => {
        console.error('Failed to enable SCIM:', error)
        if (error?.response?.status === 409) {
          onScimStateChanged?.()
          loadStatus(true)
          return
        }
        setErrorKey('sso.scim.error.enable')
      })
      .finally(() => setIsSubmitting(false))
  }

  const regenerateToken = () => {
    setErrorKey(null)
    setIsSubmitting(true)
    $http.post<ScimRotateResponse>('/authenticate/sso/scim/token', { companyId: company.id })
      .then((response) => {
        setRevealedToken(response.bearerToken)
        setView('manage')
        loadStatus(false)
      })
      .catch((error) => {
        console.error('Failed to regenerate SCIM token:', error)
        setErrorKey('sso.scim.error.regenerate')
      })
      .finally(() => setIsSubmitting(false))
  }

  const disableScim = () => {
    setErrorKey(null)
    setIsSubmitting(true)
    $http.delete('/authenticate/sso/scim', { companyId: company.id })
      .then(() => {
        setRevealedToken(null)
        onScimStateChanged?.()
        loadStatus(true)
      })
      .catch((error) => {
        console.error('Failed to disable SCIM:', error)
        setErrorKey('sso.scim.error.disable')
      })
      .finally(() => setIsSubmitting(false))
  }

  const handleClose = () => {
    if (view === 'confirmRegenerate' || view === 'confirmDisable') {
      setView('manage')
      return
    }
    setRevealedToken(null)
    onClose()
  }

  const goToConfirm = (target: View) => {
    setErrorKey(null)
    setRevealedToken(null)
    setView(target)
  }

  const titleByView: Record<View, React.ReactNode> = {
    loading: intl.formatMessage({ id: 'sso.scim.title' }),
    confirmEnable: intl.formatMessage({ id: 'sso.scim.confirmEnable.title' }),
    activeWithToken: intl.formatMessage({ id: 'sso.scim.title.activated' }),
    manage: intl.formatMessage({ id: 'sso.scim.title' }),
    confirmRegenerate: intl.formatMessage({ id: 'sso.scim.confirmRegenerate.title' }),
    confirmDisable: intl.formatMessage({ id: 'sso.scim.confirmDisable.title' }),
  }

  const contentByView: Record<View, React.ReactNode> = {
    loading: <ScimLoadingView />,
    confirmEnable: <ScimConfirmEnableView errorKey={errorKey} />,
    activeWithToken: (
      <ScimActiveWithTokenView
        revealedToken={revealedToken}
        endpointUrl={endpointUrl}
        mappingExpanded={mappingExpanded}
        onExpandMapping={() => setMappingExpanded(true)}
        errorKey={errorKey}
      />
    ),
    manage: (
      <ScimManageView
        token={token}
        revealedToken={revealedToken}
        endpointUrl={endpointUrl}
        mappingExpanded={mappingExpanded}
        onExpandMapping={() => setMappingExpanded(true)}
        onRegenerateClick={() => goToConfirm('confirmRegenerate')}
        onDisableClick={() => goToConfirm('confirmDisable')}
        errorKey={errorKey}
      />
    ),
    confirmRegenerate: <ScimConfirmRegenerateView errorKey={errorKey} />,
    confirmDisable: <ScimConfirmDisableView errorKey={errorKey} />,
  }

  const footerByView: Record<View, React.ReactNode> = {
    loading: null,
    confirmEnable: (
      <>
        <TextButton grey onClick={onClose}>
          {intl.formatMessage({ id: 'sso.scim.cancel' })}
        </TextButton>
        <TextButton loading={isSubmitting} onClick={() => enableScim()}>
          {intl.formatMessage({ id: 'sso.scim.confirmEnable.confirm' })}
        </TextButton>
      </>
    ),
    activeWithToken: (
      <TextButton onClick={() => { setRevealedToken(null); loadStatus(true) }}>
        {intl.formatMessage({ id: 'sso.scim.done' })}
      </TextButton>
    ),
    manage: null,
    confirmRegenerate: (
      <>
        <TextButton grey onClick={() => setView('manage')}>
          {intl.formatMessage({ id: 'sso.scim.cancel' })}
        </TextButton>
        <TextButton warning loading={isSubmitting} onClick={() => regenerateToken()}>
          {intl.formatMessage({ id: 'sso.scim.confirmRegenerate.confirm' })}
        </TextButton>
      </>
    ),
    confirmDisable: (
      <>
        <TextButton grey onClick={() => setView('manage')}>
          {intl.formatMessage({ id: 'sso.scim.cancel' })}
        </TextButton>
        <TextButton warning loading={isSubmitting} onClick={() => disableScim()}>
          {intl.formatMessage({ id: 'sso.scim.confirmDisable.confirm' })}
        </TextButton>
      </>
    ),
  }

  return (
    <CustomerPortalDialog
      title={titleByView[view]}
      open={open}
      onClose={handleClose}
      content={contentByView[view]}
      footer={
        <div className={styles.footer}>
          {footerByView[view]}
        </div>
      }
    />
  )
})

export default ScimManagementModal
